// The board's own menu (qa-runs#1425): the right button on an empty spot — or Shift and the right button on a token, a
// finger's long press, or the Menu key with nothing selected — opens a short menu of what may be done AT that spot. The
// token's menu's frame and look (`TokenMenu.tsx`), and like it a way into what exists: the Compendium, the ping, the
// ruler, the area template, the fog tool, the scene link form, an ordinary move, the scene's default token spot, and
// (qa-runs#1426) the DM's Set a trigger here and Pause all movement.
// Each person sees only what they may do; a player's lists nothing of the DM's tools.
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useShell } from '../shell/context';
import { useTableState } from '../table/TableState';
import { useMayAct } from '../table/mayAct';
import { menuSpot } from '../place';
import { boardBus, type BoardMenuAt } from './bus';
import { measureTool } from './measureTool';
import { areaTool } from './areaTool';
import { fogTool } from './fogTool';
import { selection } from './selection';
import { item } from './TokenMenu';
import { boardClipboard } from './clipboard';
import { newTokenSpot } from '../place';
import { useBoardMap } from './sceneMap';
import { boardExtent } from '@dragons-table/vtt-shared';
import { TriggerMenuItems } from './TriggerParts';

export function BoardMenu() {
  const { store, act, viewingAs, ping, systemRules, scene } = useTableState();
  const { table } = useShell();
  const mayAct = useMayAct();
  const [at, setAt] = useState<BoardMenuAt | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const before = useRef<HTMLElement | null>(null);
  useEffect(() => boardBus.onBoardMenu((next) => { before.current = document.activeElement as HTMLElement | null; setAt(next); }), []);
  const close = (restore = true) => { setAt(null); if (restore) before.current?.focus(); };

  // Anything that is not the menu closes it, with nothing done: a press elsewhere, Escape, or the window losing focus.
  useEffect(() => {
    if (!at) return undefined;
    ref.current?.querySelector<HTMLElement>('[role="menuitem"]:not([disabled])')?.focus();
    const away = (e: PointerEvent) => { if (!ref.current?.contains(e.target as Node)) close(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.preventDefault(); close(); } };
    const blur = () => close(true);
    document.addEventListener('pointerdown', away, true);
    document.addEventListener('keydown', esc);
    window.addEventListener('blur', blur);
    return () => { document.removeEventListener('pointerdown', away, true); document.removeEventListener('keydown', esc); window.removeEventListener('blur', blur); };
  }, [at]); // eslint-disable-line react-hooks/exhaustive-deps

  // Copy and paste (qa-runs#1428): Ctrl+C copies the selection, Ctrl+V pastes it at the default token spot (or the
  // board's middle); outside a text field only, so a box's own copy and paste work as ever. Ctrl+Z stays Undo's.
  const boardMap = useBoardMap();
  const mayPaste = !viewingAs && mayAct('objects.paste');
  const [, setCopied] = useState(0);
  useEffect(() => boardClipboard.subscribe(() => setCopied((n) => n + 1)), []);
  useEffect(() => {
    if (!mayPaste) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.shiftKey || e.altKey) return;
      const k = e.key.toLowerCase();
      if (k !== 'c' && k !== 'v') return;
      const el = e.target as HTMLElement | null;
      if (el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName))) return;
      if (window.getSelection()?.toString()) return; // text picked on the page: the browser's copy
      const objects = store.state.objects;
      if (k === 'c') {
        const things = [...selection.get()].map((id) => objects[id]).filter((o): o is NonNullable<typeof o> => Boolean(o));
        if (!things.length) return;
        e.preventDefault(); boardClipboard.copy(scene?.id ?? null, things);
      } else {
        const extent = boardExtent(boardMap);
        const body = boardClipboard.paste(scene?.id ?? null, newTokenSpot(store.state, extent), store.state, extent);
        if (!body) return;
        e.preventDefault(); act(body);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mayPaste, scene?.id, boardMap, store, act]);

  const [drawnH, setDrawnH] = useState<number | null>(null);
  useLayoutEffect(() => {
    const h = at ? ref.current?.offsetHeight || null : null;
    if (h !== drawnH) setDrawnH(h);
  });
  if (!at) return null;

  const p = at.point;
  const spot = { x: Math.round(p.x), y: Math.round(p.y) };
  const run = (fn: () => void) => () => { close(false); fn(); };
  // The DM's side of the menu needs the DM's seat (or a co-DM's right for each item), never while viewing as a player.
  const own = !viewingAs;
  const mayPlace = own && mayAct('object.add') && mayAct('object.hide');
  const mayFog = own && mayAct('fog.reveal');
  const others = (table.scenes ?? []).filter((s) => s.id !== scene?.id);
  const mayLink = own && mayAct('link.set') && others.length > 0;
  const selected = [...selection.get()].filter((id) => store.state.objects[id]);
  const mayMove = own && mayAct('object.hide') && selected.length > 0;
  // The default token spot: only those sent it may set it (the DM, a co-DM with the map right); a player never is.
  const maySpot = own && mayAct('scene.set');
  const hasSpot = Boolean(store.state.scene.tokenSpot);
  const mayArea = Boolean(systemRules.distance);

  const onKey = (e: React.KeyboardEvent) => {
    const items = Array.from(ref.current?.querySelectorAll<HTMLElement>('[role="menuitem"]:not([disabled])') ?? []);
    const i = items.indexOf(document.activeElement as HTMLElement);
    if (e.key === 'ArrowDown') { e.preventDefault(); items[(i + 1) % items.length]?.focus(); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); items[(i - 1 + items.length) % items.length]?.focus(); }
    else if (e.key === 'Home') { e.preventDefault(); items[0]?.focus(); }
    else if (e.key === 'End') { e.preventDefault(); items[items.length - 1]?.focus(); }
    else if (e.key === 'Tab') { e.preventDefault(); close(); }
  };

  const pingItem = <button type="button" role="menuitem" className={item} data-testid="board-menu-ping" onClick={run(() => ping(p.x, p.y))}>Ping here</button>;
  const W = 232; const guessH = 40 + 30 * (3 + (mayPlace ? 1 : 0) + (mayFog ? 2 : 0) + (mayLink ? 1 : 0) + (mayMove ? 1 : 0) + (maySpot ? (hasSpot ? 2 : 1) : 0) + (own ? 2 : 0));
  const H = drawnH ?? guessH;
  // Kept on screen: near the right or bottom edge it opens back towards the middle.
  const left = Math.max(4, Math.min(at.x, window.innerWidth - W - 4));
  const top = Math.max(4, Math.min(at.y, window.innerHeight - H - 4));

  return (
    <div ref={ref} role="menu" aria-label="The board: actions here" data-testid="board-menu" onKeyDown={onKey} onContextMenu={(e) => e.preventDefault()}
      className="fixed z-50 min-w-[12rem] overflow-y-auto vtt-popover py-1 shadow-xl" style={{ left, top, width: W, maxHeight: window.innerHeight - 8 }}>
      {at.touch && pingItem}
      {mayPlace && <button type="button" role="menuitem" className={item} data-testid="board-menu-place" onClick={run(() => { menuSpot.set('place', p); boardBus.showTool('compendium'); })}>Place from the Compendium here</button>}
      {!at.touch && pingItem}
      <button type="button" role="menuitem" className={item} data-testid="board-menu-measure" onClick={run(() => measureTool.from(at.cell, p))}>Measure from here</button>
      {mayArea && <button type="button" role="menuitem" className={item} data-testid="board-menu-area" onClick={run(() => { areaTool.start(); areaTool.change({ origin: at.corner }); })}>Area template here</button>}
      {mayFog && (
        <>
          <button type="button" role="menuitem" className={item} data-testid="board-menu-fog-reveal" onClick={run(() => fogTool.start('reveal'))}>Reveal fog</button>
          <button type="button" role="menuitem" className={item} data-testid="board-menu-fog-hide" onClick={run(() => fogTool.start('cover'))}>Hide fog</button>
        </>
      )}
      {mayLink && <button type="button" role="menuitem" className={item} data-testid="board-menu-link" onClick={run(() => { menuSpot.set('link', p); boardBus.showTool('scenes'); })}>Add a scene link here</button>}
      {mayPaste && boardClipboard.count(scene?.id ?? null) > 0 && <button type="button" role="menuitem" className={item} data-testid="board-menu-paste"
        onClick={run(() => { const extent = boardExtent(boardMap); const body = boardClipboard.paste(scene?.id ?? null, p, store.state, extent); if (body) act(body); })}>Paste here ({boardClipboard.count(scene?.id ?? null)})</button>}
      {/* Triggers (qa-runs#1426): the DM's Set a trigger here, and Pause all movement. */}
      <TriggerMenuItems cell={at.cell} run={run} />
      {mayMove && <button type="button" role="menuitem" className={item} data-testid="board-menu-move" onClick={run(() => boardBus.moveHere(p))}>Move selected tokens here</button>}
      {maySpot && (
        <>
          <div role="separator" className="my-1 border-t border-stone-700" />
          <button type="button" role="menuitem" className={item} data-testid="board-menu-spot" onClick={run(() => act({ kind: 'scene.set', scene: { ...store.state.scene, tokenSpot: spot } }))}>Set default token spot here</button>
          {hasSpot && <button type="button" role="menuitem" className={item} data-testid="board-menu-spot-clear" onClick={run(() => { const { tokenSpot: _t, ...rest } = store.state.scene; act({ kind: 'scene.set', scene: rest }); })}>Clear default token spot</button>}
        </>
      )}
    </div>
  );
}
